import React, { useCallback, useEffect, useState } from "react";
import { Bot, Play, RotateCw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export function NewsIngestionPanel({ onRunComplete }) {
  const [st, setSt] = useState(null);
  const [runs, setRuns] = useState([]);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: r }] = await Promise.all([api.get("/admin/news/ingest/status"), api.get("/admin/news/ingest/runs")]);
      setSt(s); setRuns(r.runs || []);
      return r.runs?.[0];
    } catch (e) { setMsg(apiErrorMessage(e, "Could not load ingestion status")); return null; }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function runNow() {
    setMsg("Running… fetching sources and drafting (up to a minute).");
    try {
      const { data } = await api.post("/admin/news/ingest/run");
      if (data.status !== "started") { setMsg(`Run ${data.status.replace("_", " ")}`); return; }
      for (let i = 0; i < 30; i += 1) {
        await new Promise((r) => setTimeout(r, 4000));
        const last = await load();
        if (last && last.id === data.run_id && last.status !== "running") {
          setMsg(`Run ${last.status}: ${last.retrieved} retrieved, ${last.new_items} new, ${last.drafts_created} drafts.`);
          onRunComplete?.();
          return;
        }
      }
      setMsg("Run still in progress — refresh shortly.");
    } catch (e) { setMsg(apiErrorMessage(e, "Run failed to start")); }
  }
  const patch = async (url, body) => { try { await api.patch(url, body); load(); } catch (e) { setMsg(apiErrorMessage(e, "Update failed")); } };

  if (!st) return null;
  return (
    <section className="glass-panel p-4 mb-6" data-testid="news-ingestion-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Bot className="h-5 w-5 text-lifted-green" />
        <h2 className="font-bebas uppercase tracking-[0.18em] text-white">AI News Ingestion</h2>
        <span className="text-[11px] text-white/50">Daily {st.settings?.schedule_utc} UTC · max {st.settings?.max_drafts_per_run} drafts/run · {st.ai_drafts_pending} AI drafts awaiting review</span>
        <label className="ml-auto text-xs text-white/70 flex items-center gap-1">
          <input type="checkbox" checked={!!st.settings?.enabled} onChange={(e) => patch("/admin/news/ingest/settings", { enabled: e.target.checked })} data-testid="ingest-enabled-toggle" />
          Daily job enabled
        </label>
        <select value={st.settings?.max_drafts_per_run || 5} onChange={(e) => patch("/admin/news/ingest/settings", { max_drafts_per_run: Number(e.target.value) })}
          className="input-lifted text-xs" data-testid="ingest-max-drafts">
          {[1, 2, 3, 5, 8, 10].map((n) => <option key={n} value={n}>{n} / run</option>)}
        </select>
        <button onClick={runNow} className="btn-lifted text-xs inline-flex items-center gap-1" data-testid="ingest-run-now"><Play className="h-3 w-3" /> Run now</button>
        <button onClick={load} className="btn-ghost text-xs" data-testid="ingest-refresh"><RotateCw className="h-3 w-3" /></button>
      </div>
      {msg && <div className="text-xs text-lifted-green mt-2" data-testid="ingest-message">{msg}</div>}
      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="eyebrow text-white/50 mb-1">Source registry</div>
          <div className="space-y-1" data-testid="ingest-sources">
            {st.sources.map((s) => (
              <div key={s.key} className="flex items-center gap-2 text-xs border border-white/10 rounded px-2 py-1.5" data-testid={`ingest-source-${s.key}`}>
                <input type="checkbox" checked={!!s.enabled} onChange={(e) => patch(`/admin/news/sources/${s.key}`, { enabled: e.target.checked })} aria-label={`Enable ${s.name}`} />
                <a href={s.homepage} target="_blank" rel="noopener noreferrer" className="text-white hover:text-lifted-green">{s.name}</a>
                <span className={s.last_status === "ok" ? "text-lifted-green" : "text-amber-300"}>{s.last_status || "unchecked"}</span>
                <span className="text-white/40 ml-auto">{s.last_item_count} items · {fmt(s.last_checked_at)}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="eyebrow text-white/50 mb-1">Recent runs</div>
          <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="ingest-runs">
            {runs.length === 0 && <div className="text-xs text-white/40">No runs yet.</div>}
            {runs.map((r) => (
              <div key={r.id} className="text-[11px] text-white/70 border border-white/10 rounded px-2 py-1">
                <span className="text-white">{r.status}</span> · {r.trigger} · {fmt(r.started_at)}
                {r.retrieved !== undefined && <> · {r.retrieved} retrieved · {r.new_items} new · {r.drafts_created} drafts</>}
                {r.errors?.length > 0 && <div className="text-amber-300">{r.errors.join("; ")}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
